import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useApiData } from 'reducers/hooks'
import { getSitesThunk } from 'reducers/fell/sites/action'
import { selectSites, selectStatus } from 'reducers/fell/sites/sitesSlice'
import { CameraRecordingDataRow, GetCalculationPayload, ProposalsProps, ServerSettingsData, SiteDesignSettingsData } from '../../lib/types'
import { getProjectsThunk } from '../../reducers/fell/projects/action'
import { exportSiteDesignThunk, fetchSiteDesign, getSiteDesigns, saveChangesSiteDesign } from '../../reducers/fell/siteDesign/siteDesignActions'
import { selectCalculationData, selectCalculationStatus } from '../../reducers/fell/proposals/proposalSlice'
import { getCalculation } from '../../reducers/fell/proposals/proposalsActions'
import { selectSiteDesignStatus, selectSiteDesigns } from '../../reducers/fell/siteDesign/siteDesignSlice'
import { buildSiteDesignExportFileName, decodeJsonBytes, downloadBlob } from '../../lib/helpers'

type SiteDesignEditorData = {
  projectIdNumber
  projectName
  siteIdNumber
  siteName
  siteDesignIdNumber
  siteDesignName
  siteDesignData
  proposalPropsData: ProposalsProps
  saveSettingsButtonLabel
  onGenerateProposalButtonClick
  modifiedCameraRecordings: CameraRecordingDataRow[]
  setModifiedCameraRecordings: React.Dispatch<React.SetStateAction<CameraRecordingDataRow[]>>
  setModifiedServerSettings: React.Dispatch<React.SetStateAction<ServerSettingsData>>
  updatedCameraAmount
  hardwareType: number
  hasManagementServerValidationError
  setHasManagementServerValidationError: React.Dispatch<React.SetStateAction<boolean>>
  saveError: string | null
  showSaveErrorModal: boolean
  setShowSaveErrorModal: React.Dispatch<React.SetStateAction<boolean>>
  handleExportSiteDesign: () => Promise<void>
  errorModalHeadline: string
  isExporting: boolean
}

export const SiteDesignEditorInitialization = (): SiteDesignEditorData => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { projectId, siteId, siteDesignId } = useParams()
  const projectIdNumber = projectId ? parseInt(projectId) : null
  const siteIdNumber = siteId ? parseInt(siteId) : null
  const siteDesignIdNumber = siteDesignId ? parseInt(siteDesignId) : null

  const projectsData = useApiData(
    getProjectsThunk(),
    state => state.projects.data,
    state => state.projects.projectsStatus,
    navigate
  ) // Pass navigate to useApiData
  const projectName = projectsData.find(project => project.id.toString() === projectId)?.name

  const sitesData = useApiData(getSitesThunk(projectId), selectSites(projectId), selectStatus(projectId), navigate)
  const siteName = sitesData?.find(site => site.id.toString() === siteId)?.name

  const siteDesigns = useApiData(
    getSiteDesigns({ projectId: projectIdNumber, siteId: siteIdNumber }),
    selectSiteDesigns(projectIdNumber, siteIdNumber),
    selectSiteDesignStatus(projectIdNumber, siteIdNumber),
    navigate
  )
  const siteDesignName = siteDesigns?.find(siteDesign => siteDesign.id.toString() === siteDesignId)?.name

  const selectSiteDesignData = state => {
    if (projectIdNumber && siteIdNumber && siteDesignIdNumber) {
      return state.siteDesignEditor.data[projectIdNumber]?.[siteIdNumber]?.[siteDesignIdNumber]
    }
    return null
  }

  useEffect(() => {
    if (projectIdNumber && siteIdNumber && siteDesignIdNumber) {
      const getSiteDesignSetup = async () => {
        try {
          const result = await dispatch(
            fetchSiteDesign({
              projectId: projectIdNumber,
              siteId: siteIdNumber,
              siteDesignId: siteDesignIdNumber
            })
          )

          if (result.meta.requestStatus === 'rejected') {
            console.warn('Request for fetchSiteDesign was rejected')
            navigate('/error-page')
          }
        } catch (error) {
          console.error('Error in getSiteDesignSetup:', error)
          navigate('/error-page')
        }
      }

      getSiteDesignSetup()
    }
  }, [dispatch, projectIdNumber, siteIdNumber, siteDesignIdNumber, navigate])

  const [saveSettingsButtonLabel, setSaveSettingsButtonLabel] = useState<string>('Generate Proposal')
  const [calculationData, setCalculationData] = useState<GetCalculationPayload>()
  const siteDesignData = useSelector(selectSiteDesignData)
  const [hardwareType, setHardwareType] = useState(siteDesignData?.siteDesign?.serverSettings?.hardwareType)
  const [proposalPropsData, setproposalPropsData] = useState<ProposalsProps>()
  useEffect(() => {
    if (siteDesignData && siteDesignData.projectId && siteDesignData.siteId && siteDesignData.siteDesignId && calculationData) {
      const proposalPropsData: ProposalsProps = {
        siteDesignData: siteDesignData,
        calculationData: calculationData
      }
      setproposalPropsData(proposalPropsData)
    }
    if (siteDesignData?.siteDesign?.cameras) {
      setUpdatedCameraAmount(siteDesignData?.siteDesign?.cameras?.reduce((sum, camera) => sum + camera?.quantity, 0))
    }
    if (siteDesignData?.siteDesign?.serverSettings) {
      setHardwareType(siteDesignData?.siteDesign?.serverSettings?.hardwareType)
    }
  }, [siteDesignData, calculationData])

  const calculateProposal = () => {
    if (siteDesignData && siteDesignData.projectId && siteDesignData.siteId && siteDesignData.siteDesignId) {
      dispatch(
        getCalculation({
          projectId: siteDesignData.projectId,
          siteId: siteDesignData.siteId,
          siteDesignId: siteDesignId
        }),
        selectCalculationData,
        selectCalculationStatus
      )
        .then(response => {
          // we don't use await as we need to save the response
          setCalculationData(response?.payload)
          setSaveSettingsButtonLabel('Update Proposal')
        })
        .catch(error => {
          console.error('Error fetching calculation data', error)
          navigate('/error-page')
        })
    }
  }

  //Called to generate a proposal
  //First, we check and build the siteDesign object to post. This object will contain all pending changes to besaved before getting the calculation
  //if any changes have been made then we check and get the modified camera group(s) / camera streams
  //if any changes have been made in the server settings then we get all sever settings
  //THEN
  //with or without changes, we will get the calculation
  const [modifiedCameraRecordings, setModifiedCameraRecordings] = useState<CameraRecordingDataRow[]>(null)
  const [modifiedServerSettings, setModifiedServerSettings] = useState<ServerSettingsData>(null)
  const [modifiedSiteDesign, setModifiedSiteDesign] = useState<SiteDesignSettingsData>()
  const [updatedCameraAmount, setUpdatedCameraAmount] = useState<number>(null)
  const [hasManagementServerValidationError, setHasManagementServerValidationError] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showSaveErrorModal, setShowSaveErrorModal] = useState(false)
  const [errorModalHeadline, setErrorModalHeadline] = useState<string>('Unable to Save Changes')
  const [isExporting, setIsExporting] = useState(false)
  useEffect(() => {
    const modifiedSiteDesignData: SiteDesignSettingsData = {
      cameras: modifiedCameraRecordings,
      serverSettings: modifiedServerSettings
    }
    setModifiedSiteDesign(modifiedSiteDesignData)

    if (modifiedCameraRecordings) {
      const modifiedMap = new Map(modifiedCameraRecordings.map(cam => [cam.streamId, cam.quantity]))
      setUpdatedCameraAmount(
        siteDesignData?.siteDesign?.cameras?.reduce((sum, cam) => {
          const quantity = modifiedMap.get(cam.streamId) ?? cam.quantity
          return sum + quantity
        }, 0)
      )
    }

    //hide the proposal section when changing the hardware type
    if (modifiedServerSettings && modifiedServerSettings.hardwareType !== siteDesignData?.siteDesign?.serverSettings?.hardwareType) {
      setHardwareType(modifiedServerSettings.hardwareType)
      setproposalPropsData(null)
    }
  }, [modifiedCameraRecordings, modifiedServerSettings, siteDesignData?.siteDesign?.cameras, siteDesignData?.siteDesign?.serverSettings?.hardwareType])

  //Saves any pending camera and server settings changes.
  //Returns true when there was nothing to save or the save succeeded, false when the save failed.
  //On failure the error modal is already showing, so callers should simply stop.
  const saveModifiedSiteDesign = async (): Promise<boolean> => {
    if (!modifiedSiteDesign || (!modifiedSiteDesign.cameras && !modifiedSiteDesign.serverSettings)) {
      return true
    }

    try {
      setSaveError(null)
      setShowSaveErrorModal(false)

      await dispatch(
        saveChangesSiteDesign({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber,
          siteDesignData: modifiedSiteDesign
        })
      ).unwrap()

      setModifiedCameraRecordings(null)
      setModifiedServerSettings(null)

      await dispatch(
        fetchSiteDesign({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber
        })
      ).unwrap()

      return true
    } catch (error) {
      console.error('Failed to save changes', error)
      setErrorModalHeadline('Unable to Save Changes')
      setSaveError('Failed to save changes. Please contact support.')
      setShowSaveErrorModal(true)
      return false
    }
  }

  const onGenerateProposalButtonClick = async () => {
    const didSave = await saveModifiedSiteDesign()
    if (!didSave) return

    calculateProposal()
  }

  const handleExportSiteDesign = async () => {
    if (isExporting) return

    setIsExporting(true)

    try {
      const didSave = await saveModifiedSiteDesign()
      if (!didSave) return

      const siteDesignBlob = await dispatch(
        exportSiteDesignThunk({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber
        })
      ).unwrap()

      //The response body is UTF-16 LE, so it is transcoded to UTF-8 before download - saving the
      //raw bytes produces a file that editors and JSON parsers read as garbage.
      const siteDesignJson = decodeJsonBytes(await siteDesignBlob.arrayBuffer())

      downloadBlob(new Blob([siteDesignJson], { type: 'application/json' }), buildSiteDesignExportFileName(siteDesignName, siteDesignIdNumber))
    } catch (error: any) {
      console.error('Failed to export site design', error)
      setErrorModalHeadline('Unable to Export Site Design')
      setSaveError(error?.error || 'Failed to export the site design. Please try again.')
      setShowSaveErrorModal(true)
    } finally {
      setIsExporting(false)
    }
  }

  //Handle the leave from Site design editor to anywhere else including closing the tab and the browser.
  useEffect(() => {
    // Minimal setup to trigger warning - Anyhow, as it is the "beforeunload" the browsers for secuirty reasons don't allow to customize the warning text
    const handleBeforeUnload = event => {
      event.preventDefault()
      event.returnValue = ''
    }

    const hasUnsavedChanges = modifiedSiteDesign && (modifiedSiteDesign.cameras || modifiedSiteDesign.serverSettings)
    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [location.pathname, navigate, modifiedSiteDesign])

  return {
    projectIdNumber,
    projectName,
    siteIdNumber,
    siteName,
    siteDesignIdNumber,
    siteDesignName,
    siteDesignData,
    proposalPropsData,
    saveSettingsButtonLabel,
    onGenerateProposalButtonClick,
    modifiedCameraRecordings,
    setModifiedCameraRecordings,
    setModifiedServerSettings,
    updatedCameraAmount,
    hardwareType,
    hasManagementServerValidationError,
    setHasManagementServerValidationError,
    saveError,
    showSaveErrorModal,
    setShowSaveErrorModal,
    handleExportSiteDesign,
    errorModalHeadline,
    isExporting
  }
}
